import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { listedConnectorDirectory } from "../lib/connectorDirectory";
import {
  activeTriggerToken,
  slashCompletionSuffix,
  type SlashToken,
} from "../lib/slashToken";

export type MentionStep = { name: string; color: string };
export type MentionConnector = { name: string; identifier: string };
export type MentionConnectorCatalogEntry = {
  name: string;
  identifier: string;
  keywords?: string;
  popular?: boolean;
};

export const mentionConnectorCatalog: readonly MentionConnectorCatalogEntry[] =
  listedConnectorDirectory.map((c) => ({
    name: c.name,
    identifier: c.identifier,
    keywords: c.definition.keywords,
    popular: c.tags.includes("featured"),
  }));

export type MentionConnectorSection = {
  label: string;
  items: readonly MentionConnectorCatalogEntry[];
};

export type MentionItem =
  | { kind: "step"; id: string; name: string; color: string; section: string }
  | {
      kind: "connector";
      id: string;
      name: string;
      identifier: string;
      section: string;
    };

export type MentionCompletionState = {
  token: SlashToken | null;
  matches: readonly MentionItem[];
  selectedIndex: number;
  caret: number;
  suffix: string;
  catalogTotal: number;
  onSelect: (item: MentionItem) => void;
};

const STEPS_SECTION = "Steps";
const AVAILABLE_SECTION = "Available connectors";
const ALL_SECTION = "All connectors";
const POPULAR_SAMPLE_COUNT = 8;
const CATALOG_SEARCH_LIMIT = 25;

type ConnectorSections = readonly [
  MentionConnectorSection,
  MentionConnectorSection,
  MentionConnectorSection,
];

function shufflePopular(
  catalog: readonly MentionConnectorCatalogEntry[] | undefined
) {
  const popular = (catalog ?? [])
    .filter((c) => c.popular)
    .map((c) => ({ name: c.name, identifier: c.identifier }));
  // Fisher–Yates: an even sample, unlike sort(() => Math.random() - 0.5).
  for (let i = popular.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [popular[i], popular[j]] = [popular[j]!, popular[i]!];
  }
  return popular;
}

function catalogRank(c: MentionConnectorCatalogEntry, q: string) {
  const name = c.name.toLowerCase();
  if (name.startsWith(q)) return 0;
  if (name.includes(` ${q}`)) return 1;
  if (name.includes(q)) return 2;
  if ((c.keywords ?? "").toLowerCase().includes(q)) return 3;
  return null;
}

function connectorMatches(
  [context, tenant, catalog]: ConnectorSections,
  popularOrder: readonly MentionConnector[],
  q: string,
  browseAll = false
): MentionConnectorSection[] {
  const nameMatches = (c: MentionConnector) => c.name.toLowerCase().includes(q);
  const catalogNames = new Set(catalog.items.map((c) => c.name));
  // Browsing shows the popular sample unless asked for everything; typing
  // searches the whole catalog by name and keywords. Either way skip ones
  // listed above.
  const catalogItems =
    q === "" && browseAll
      ? catalog.items
      : q === ""
        ? popularOrder
            .filter((c) => catalogNames.has(c.name))
            .slice(0, POPULAR_SAMPLE_COUNT)
        : catalog.items
            .flatMap((c) => {
              const rank = catalogRank(c, q);
              return rank === null ? [] : [{ c, rank }];
            })
            .sort((a, b) => a.rank - b.rank)
            .slice(0, CATALOG_SEARCH_LIMIT)
            .map(({ c }) => c);
  return [
    { label: context.label, items: context.items.filter(nameMatches) },
    { label: tenant.label, items: tenant.items.filter(nameMatches) },
    { label: catalog.label, items: catalogItems },
  ];
}

type UseMentionsParams = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (v: string) => void;
  steps: readonly MentionStep[];
  connectors: readonly MentionConnector[];
  tenantConnectors?: readonly MentionConnector[];
  connectorCatalog?: readonly MentionConnectorCatalogEntry[];
  contextLabel?: string;
  onAfterAccept?: () => void;
  enabled?: boolean;
};

export function useMentions({
  textareaRef,
  value,
  setValue,
  steps,
  connectors,
  tenantConnectors,
  connectorCatalog,
  contextLabel = "On this workflow",
  onAfterAccept,
  enabled = true,
}: UseMentionsParams) {
  const [token, setToken] = useState<SlashToken | null>(null);

  const stepItems = useMemo<MentionItem[]>(
    () =>
      enabled
        ? steps.map((s) => ({
            kind: "step" as const,
            id: `step:${s.name}`,
            name: s.name,
            color: s.color,
            section: STEPS_SECTION,
          }))
        : [],
    [enabled, steps]
  );

  const [selectedSteps, setSelectedSteps] = useState<MentionStep[]>([]);
  const addStep = useCallback((step: MentionStep) => {
    setSelectedSteps((prev) =>
      prev.some((s) => s.name === step.name) ? prev : [...prev, step]
    );
  }, []);
  const removeStep = useCallback((name: string) => {
    setSelectedSteps((prev) => prev.filter((s) => s.name !== name));
  }, []);
  const clearSteps = useCallback(() => setSelectedSteps([]), []);

  const [selectedConnectors, setSelectedConnectors] = useState<
    MentionConnector[]
  >([]);
  const addConnector = useCallback((connector: MentionConnector) => {
    setSelectedConnectors((prev) =>
      prev.some((c) => c.name === connector.name) ? prev : [...prev, connector]
    );
  }, []);
  const removeConnector = useCallback((name: string) => {
    setSelectedConnectors((prev) => prev.filter((c) => c.name !== name));
  }, []);
  const clearConnectors = useCallback(() => setSelectedConnectors([]), []);

  const syncMentionToken = useCallback(
    (el: HTMLTextAreaElement) => {
      if (!enabled) return;
      const next =
        el.value === ""
          ? null
          : activeTriggerToken(el.value, el.selectionStart ?? 0, "@");
      setToken((prev) =>
        prev &&
        next &&
        prev.start === next.start &&
        prev.end === next.end &&
        prev.query === next.query
          ? prev
          : next
      );
    },
    [enabled]
  );

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!enabled || !el) return;
    syncMentionToken(el);
  }, [enabled, syncMentionToken, textareaRef]);

  const [popularOrder, setPopularOrder] = useState(() =>
    shufflePopular(connectorCatalog)
  );
  const menuOpenRef = useRef(false);
  const menuOpen = token !== null;
  // Draw a fresh popular shuffle once per "@" open — held in state so it stays
  // put across re-renders instead of reshuffling on every one.
  useLayoutEffect(() => {
    if (menuOpen && !menuOpenRef.current) {
      setPopularOrder(shufflePopular(connectorCatalog));
    }
    menuOpenRef.current = menuOpen;
  }, [menuOpen, connectorCatalog]);

  const connectorSections = useMemo((): ConnectorSections => {
    const contextNames = new Set(connectors.map((c) => c.name));
    const tenant = (tenantConnectors ?? []).filter(
      (c) => !contextNames.has(c.name)
    );
    const shownNames = new Set([...contextNames, ...tenant.map((c) => c.name)]);
    const catalog = (connectorCatalog ?? []).filter(
      (c) => !shownNames.has(c.name)
    );
    return [
      { label: contextLabel, items: connectors },
      { label: AVAILABLE_SECTION, items: tenant },
      { label: ALL_SECTION, items: catalog },
    ];
  }, [connectors, tenantConnectors, connectorCatalog, contextLabel]);

  const searchConnectors = useCallback(
    (query: string, browseAll: boolean) =>
      connectorMatches(
        connectorSections,
        popularOrder,
        query.toLowerCase(),
        browseAll
      ),
    [connectorSections, popularOrder]
  );

  const matches = useMemo<MentionItem[]>(() => {
    if (!token) return [];
    const q = token.query.toLowerCase();
    const stepMatches = stepItems.filter((i) =>
      i.name.toLowerCase().includes(q)
    );
    const connectorItems = connectorMatches(
      connectorSections,
      popularOrder,
      q
    ).flatMap((section) =>
      section.items.map((c): MentionItem => ({
        kind: "connector",
        id: `connector:${c.identifier}:${c.name}`,
        name: c.name,
        identifier: c.identifier,
        section: section.label,
      }))
    );
    return [...stepMatches, ...connectorItems];
  }, [token, stepItems, connectorSections, popularOrder]);

  const [selected, setSelected] = useState(0);
  const tokenKey = `${token?.start} ${token?.query}`;
  const [prevTokenKey, setPrevTokenKey] = useState(tokenKey);
  if (tokenKey !== prevTokenKey) {
    setPrevTokenKey(tokenKey);
    setSelected(0);
  }
  const selectedIndex =
    matches.length > 0 ? Math.min(selected, matches.length - 1) : 0;

  const caret = token ? token.start + 1 + token.query.length : 0;
  const suffix = slashCompletionSuffix(
    token,
    caret,
    matches.length > 0 ? matches[selectedIndex]!.name : null
  );

  const acceptMention = useCallback(
    (item: MentionItem) => {
      if (!token) return;
      const before = value.slice(0, token.start);
      const after = value.slice(token.end);
      // Add a trailing space only at the end of the input; mid-message there's already whitespace after the token.
      const inserted = `@${item.name}${after === "" ? " " : ""}`;
      const caret = before.length + inserted.length;
      setValue(before + inserted + after);
      if (item.kind === "step") addStep({ name: item.name, color: item.color });
      else addConnector({ name: item.name, identifier: item.identifier });
      setToken(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(caret, caret);
        }
        onAfterAccept?.();
      });
    },
    [token, value, addStep, addConnector, textareaRef, setValue, onAfterAccept]
  );

  const handleMentionKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (matches.length === 0) return false;
      const atEnd =
        e.currentTarget.selectionStart === e.currentTarget.selectionEnd &&
        e.currentTarget.selectionStart === token?.end;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((i) => Math.min(i + 1, matches.length - 1));
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((i) => Math.max(i - 1, 0));
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        acceptMention(matches[selectedIndex]!);
        return true;
      }
      if (e.key === "ArrowRight" && atEnd && suffix !== "") {
        e.preventDefault();
        acceptMention(matches[selectedIndex]!);
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setToken(null);
        return true;
      }
      return false;
    },
    [matches, selectedIndex, acceptMention, token, suffix]
  );

  const catalogTotal = connectorCatalog?.length ?? 0;

  return {
    selectedSteps,
    addStep,
    removeStep,
    clearSteps,
    selectedConnectors,
    addConnector,
    removeConnector,
    clearConnectors,
    searchConnectors,
    syncMentionToken,
    mentionCompletion: {
      token,
      matches,
      selectedIndex,
      caret,
      suffix,
      catalogTotal,
      onSelect: acceptMention,
    } satisfies MentionCompletionState,
    handleMentionKeyDown,
  };
}
